/**
 * The deep-review hold (YAG-372, prototype `#/w3`).
 *
 * When a review run has drafted findings and the whole draft is waiting on an
 * editor, the pane shows one card per finding and lets a person keep, edit, or
 * remove each of them before the review is posted. The decisions ride the SAME
 * gate resolve as the verdict, into the gate's own `resolution` JSONB.
 *
 * It renders only when findings actually exist. The producer of the
 * `review_findings` artifact is a separate workstream, so on most items the
 * read comes back empty and the ordinary result-review gate is what shows.
 * Nothing here fabricates a review that has not been drafted.
 */
import { useCallback, useEffect, useState } from "react";
import type {
  ReviewFinding,
  ReviewFindingDecision,
  ReviewFindingResolution,
  ReviewFindingsResponse,
  WorkItemReviewThread,
} from "@yagni/shared";
import {
  discardedFindingsLine,
  orchestrationLine,
  REVIEW_DECISION_NOTE,
  REVIEW_DRAFTED_VERB,
  REVIEW_EDIT,
  REVIEW_HELD_NOTE,
  REVIEW_KEEP,
  REVIEW_REMOVE,
  REVIEW_TEACH_NOTE,
  REVIEW_THREAD_JUDGMENT,
  REVIEW_THREAD_WRIGHT_REPLIED,
  reviewThreadsGroupLabel,
} from "./workCopy.js";
import { Finding } from "../ui/index.js";
import { useWorkerLookup } from "./useWorkerLookup.js";
import { WorkerChip } from "./WorkerChip.js";

/**
 * The findings list's alignment contract.
 *
 * A finding's check ("tests", "minimalism") is the column a reader scans, and
 * a column-flex card let every body start its prose wherever that card's
 * chips happened to end. The list is a two-column grid: the meta column is a
 * FIXED width so the checks stack into a readable rail, and the text column
 * takes whatever is left. The rule lives in `work.css`, scoped to this class,
 * so the shared `Finding` primitive is untouched wherever else it appears.
 *
 * It used to ride a second `is-aligned` modifier that every caller set and
 * nobody ever unset — a variant with one value, which is not a variant.
 */
export const FINDINGS_LIST_CLASS = "wk-review-findings";

export interface ReviewFindingsState {
  findings: ReviewFinding[];
  orchestration: ReviewFindingsResponse["orchestration"];
  /** The worker whose review run drafted this, or null when it named none. */
  personaSlug: string | null;
  decisions: Map<string, { decision: ReviewFindingDecision; body: string }>;
  setDecision: (findingId: string, decision: ReviewFindingDecision) => void;
  setBody: (findingId: string, body: string) => void;
  /** What rides the gate resolve. Empty when there is nothing to record. */
  resolutions: ReviewFindingResolution[];
}

/**
 * Read the drafted findings for one item. `enabled` is false unless the OPEN
 * gate is a result review, so an item without a held review never makes the
 * request at all.
 */
export function useReviewFindings(
  workItemId: string,
  enabled: boolean,
): ReviewFindingsState {
  const [findings, setFindings] = useState<ReviewFinding[]>([]);
  const [orchestration, setOrchestration] =
    useState<ReviewFindingsResponse["orchestration"]>(null);
  const [personaSlug, setPersonaSlug] = useState<string | null>(null);
  const [decisions, setDecisions] = useState<
    Map<string, { decision: ReviewFindingDecision; body: string }>
  >(() => new Map());

  useEffect(() => {
    if (!enabled) {
      setFindings([]);
      setOrchestration(null);
      setPersonaSlug(null);
      return;
    }
    let cancelled = false;
    fetch(`/api/work-items/${encodeURIComponent(workItemId)}/review-findings`, {
      credentials: "include",
    })
      .then((res) => (res.ok ? (res.json() as Promise<ReviewFindingsResponse>) : null))
      .then((body) => {
        if (cancelled || !body) return;
        const rows = Array.isArray(body.findings) ? body.findings : [];
        setFindings(rows);
        setOrchestration(body.orchestration ?? null);
        setPersonaSlug(typeof body.personaSlug === "string" ? body.personaSlug : null);
        // Every finding starts kept. That is what the draft says, and a default
        // of "kept" is the only one that does not put words in an editor's
        // mouth before they have read it.
        setDecisions(
          new Map(rows.map((row) => [row.id, { decision: "keep" as const, body: row.body }])),
        );
      })
      .catch(() => {
        if (!cancelled) setFindings([]);
      });
    return () => {
      cancelled = true;
    };
  }, [workItemId, enabled]);

  const setDecision = useCallback((findingId: string, decision: ReviewFindingDecision) => {
    setDecisions((current) => {
      const next = new Map(current);
      const existing = next.get(findingId);
      next.set(findingId, { decision, body: existing?.body ?? "" });
      return next;
    });
  }, []);

  const setBody = useCallback((findingId: string, body: string) => {
    setDecisions((current) => {
      const next = new Map(current);
      const existing = next.get(findingId);
      next.set(findingId, { decision: existing?.decision ?? "edited", body });
      return next;
    });
  }, []);

  const resolutions: ReviewFindingResolution[] = findings.map((finding) => {
    const state = decisions.get(finding.id);
    const decision = state?.decision ?? "keep";
    return decision === "edited"
      ? { findingId: finding.id, decision, body: state?.body ?? finding.body }
      : { findingId: finding.id, decision };
  });

  return {
    findings,
    orchestration,
    personaSlug,
    decisions,
    setDecision,
    setBody,
    resolutions,
  };
}

export function ReviewFindingsCards({ state }: { state: ReviewFindingsState }) {
  const { findings, orchestration, personaSlug, decisions, setDecision, setBody } = state;
  // The chip resolves through the worker directory, so the name and mark are
  // the registry's (and a workspace rename shows here too). An unknown slug
  // draws nothing rather than a craft word this file made up.
  const workerOf = useWorkerLookup();
  if (findings.length === 0) return null;
  const line = orchestration ? orchestrationLine(orchestration) : null;

  return (
    <div className="wk-review" data-testid="review-findings">
      <WorkerChip worker={workerOf(personaSlug)} verb={REVIEW_DRAFTED_VERB} />
      <p className="wk-review-note">{REVIEW_HELD_NOTE}</p>
      {/* Only from counts the artifact actually recorded. */}
      {line && (
        <p className="wk-review-orch" data-testid="review-orchestration">
          {line}
        </p>
      )}
      <ul className={FINDINGS_LIST_CLASS} data-testid="review-findings-list">
        {findings.map((finding) => {
          const state_ = decisions.get(finding.id);
          const decision = state_?.decision ?? "keep";
          return (
            <Finding
              key={finding.id}
              testId={`review-finding-${finding.id}`}
              severity={finding.severity}
              check={finding.check}
              confidence={finding.confidence}
              file={finding.file}
              line={finding.line}
              body={finding.body}
              snippet={finding.snippet}
              dimmed={decision === "removed"}
              mark={decision !== "keep" ? REVIEW_DECISION_NOTE[decision] : null}
              actions={
                <>
                  {(["keep", "edited", "removed"] as const).map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={"wk-ccard-act" + (decision === option ? " is-on" : "")}
                      aria-pressed={decision === option}
                      onClick={() => setDecision(finding.id, option)}
                    >
                      {option === "keep"
                        ? REVIEW_KEEP
                        : option === "edited"
                          ? REVIEW_EDIT
                          : REVIEW_REMOVE}
                    </button>
                  ))}
                </>
              }
            >
              {decision === "edited" ? (
                <textarea
                  className="wk-ccard-edit"
                  value={state_?.body ?? finding.body}
                  onChange={(event) => setBody(finding.id, event.target.value)}
                  rows={3}
                  aria-label={`Rewrite the ${finding.check ?? "review"} finding`}
                />
              ) : undefined}
            </Finding>
          );
        })}
      </ul>
      <p className="wk-review-note">{REVIEW_TEACH_NOTE}</p>
    </div>
  );
}

/**
 * The findings a posted review carried, read only.
 *
 * The held draft above is a thing to decide; this is a thing that happened,
 * so it has no verbs. `dropped` is the report's own recorded count of what
 * the editor threw out, and it prints one quiet line or none at all — an
 * older report that never wrote the count says nothing rather than zero.
 *
 * `threads`, when the read has them, replaces `findings`: every comment
 * Proctor posted on the pull request, every round, with the severity GitHub
 * got and the thread's state there, so the page and the pull request say the
 * same thing. Open threads first, then resolved ones.
 */
export function PostedFindings({
  findings,
  dropped,
  threads,
}: {
  findings: ReviewFinding[];
  dropped?: number | null;
  threads?: WorkItemReviewThread[] | null;
}) {
  const discarded = discardedFindingsLine(dropped);
  if (threads && threads.length > 0) {
    const groups = [
      { resolved: false, rows: threads.filter((thread) => thread.isResolved !== true) },
      { resolved: true, rows: threads.filter((thread) => thread.isResolved === true) },
    ].filter((group) => group.rows.length > 0);
    return (
      <>
        {groups.map((group) => (
          <div key={group.resolved ? "resolved" : "open"} data-testid={`posted-threads-${group.resolved ? "resolved" : "open"}`}>
            <p className="wk-review-orch">{reviewThreadsGroupLabel(group.resolved, group.rows.length)}</p>
            <ul className={FINDINGS_LIST_CLASS}>
              {group.rows.map((thread) => (
                <Finding
                  key={thread.commentId}
                  testId={`posted-thread-${thread.commentId}`}
                  severity={thread.severity}
                  file={thread.path}
                  line={thread.line}
                  body={thread.body}
                  mark={threadMark(thread)}
                />
              ))}
            </ul>
          </div>
        ))}
        {discarded && (
          <p className="wk-review-orch" data-testid="posted-findings-discarded">
            {discarded}
          </p>
        )}
      </>
    );
  }
  if (findings.length === 0 && !discarded) return null;
  return (
    <>
      {findings.length > 0 && (
        <ul className={FINDINGS_LIST_CLASS} data-testid="posted-findings-list">
          {findings.map((finding, index) => (
            <Finding
              key={finding.id || `${finding.check ?? "finding"}-${index}`}
              testId={`posted-finding-${finding.id || index}`}
              severity={finding.severity}
              check={finding.check}
              confidence={finding.confidence}
              file={finding.file}
              line={finding.line}
              body={finding.body}
              snippet={finding.snippet}
            />
          ))}
        </ul>
      )}
      {discarded && (
        <p className="wk-review-orch" data-testid="posted-findings-discarded">
          {discarded}
        </p>
      )}
    </>
  );
}

/** One footnote per thread: Wright's reply and Proctor's latest judgment. */
function threadMark(thread: WorkItemReviewThread): string | null {
  const parts = [
    thread.wrightReplied ? REVIEW_THREAD_WRIGHT_REPLIED : null,
    thread.classification ? (REVIEW_THREAD_JUDGMENT[thread.classification] ?? null) : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}
